import ModalButton from './ModalButton';
import ProjectForm from './ProjectForm';
import PropTypes from 'prop-types';
import React, {useContext, useMemo, useState} from 'react';
import {
  BILLING_WRAPPER_PROPS,
  CARD_ELEMENT_WRAPPER_PROPS,
  ProjectsContext,
  UserContext,
  useCardElementOptions,
  useCreatePaymentMethod
} from '../utils';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormHelperText,
  Heading,
  ListItem,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Stack,
  Text,
  UnorderedList
} from '@chakra-ui/react';
import {CardElement} from '@stripe/react-stripe-js';
import {FiArrowRight, FiPlus} from 'react-icons/fi';
import {LIST_PROJECTS, PROJECT_FRAGMENT} from '../utils/queries';
import {PaymentMethodControl} from './PaymentMethod';
import {gql} from '@apollo/client';
import {graphql, navigate, useStaticQuery} from 'gatsby';

const CREATE_PROJECT = gql`
  mutation CreateProject(
    $input: ProjectInput!
    $file: FileInput
    $isTrial: Boolean!
  ) {
    createProject(isTrial: $isTrial, file: $file, input: $input) {
      ...ProjectFragment
    }
  }
  ${PROJECT_FRAGMENT}
`;

function NewProjectForm({onCancel}) {
  const projects = useContext(ProjectsContext);
  const canCreateTrial = useMemo(
    () => projects.every(project => !project.isTrial),
    [projects]
  );
  const [trial, setTrial] = useState(!projects.length);
  const cardElementOptions = useCardElementOptions();
  const {user} = useContext(UserContext);

  const [
    createPaymentMethod,
    {loading, ready, error}
  ] = useCreatePaymentMethod();

  const {config} = useStaticQuery(
    graphql`
      {
        config {
          paidProjectCost
          freeFileSizeLimit
        }
      }
    `
  );

  return (
    <>
      <ModalBody>
        <Box p="3" {...BILLING_WRAPPER_PROPS}>
          <Flex align="center">
            <Heading fontSize="xl" fontWeight="semibold">
              {trial ? 'Trial' : 'Pro'} project {trial ? '🌱' : '💎'}
            </Heading>
            {canCreateTrial && (
              <Button
                size="xs"
                ml="auto"
                colorScheme={trial ? 'indigo' : 'green'}
                onClick={() => setTrial(prev => !prev)}
              >
                Switch to {trial ? 'Pro' : 'trial'}
              </Button>
            )}
          </Flex>
          <Text mb="4">
            {trial
              ? 'Take Playback for a spin, for free.'
              : `$${config.paidProjectCost} one time payment, yours forever.`}
          </Text>
          {trial ? (
            <Stack fontSize="sm">
              <Text>
                A trial project will allow you to test out Playback&apos;s
                features, but comes with a few restrictions:
              </Text>
              <div>
                <UnorderedList>
                  <ListItem>
                    {config.freeFileSizeLimit} MB audio file size limit
                  </ListItem>
                  <ListItem>
                    Can&apos;t share project with other accounts
                  </ListItem>
                  <ListItem>Can&apos;t download project as a PDF</ListItem>
                  <ListItem>Limit 1 per account</ListItem>
                </UnorderedList>
              </div>
              <Text>
                If you decide to start with a trial now, you can always upgrade
                to a Pro project later.
              </Text>
            </Stack>
          ) : user.paymentMethod ? (
            <PaymentMethodControl paymentMethod={user.paymentMethod} />
          ) : (
            <>
              {error && (
                <Text color="red.500" mb="2">
                  {error.message}
                </Text>
              )}
              <FormControl isRequired>
                <Box {...CARD_ELEMENT_WRAPPER_PROPS}>
                  <CardElement options={cardElementOptions} />
                </Box>
                <FormHelperText>
                  Enter your payment details to continue
                </FormHelperText>
              </FormControl>
            </>
          )}
        </Box>
      </ModalBody>
      {user.paymentMethod || trial ? (
        <ProjectForm
          isTrial={trial}
          buttonText={`Create ${
            trial ? 'trial project' : `project ($${config.paidProjectCost})`
          }`}
          onCancel={onCancel}
          mutation={CREATE_PROJECT}
          mutationOptions={{
            onCompleted: data => navigate(`/projects/${data.createProject.id}`),
            update(cache, {data}) {
              const queryOptions = {query: LIST_PROJECTS};
              const {projects} = cache.readQuery(queryOptions);
              cache.writeQuery({
                ...queryOptions,
                data: {
                  projects: [data.createProject, ...projects]
                }
              });
            }
          }}
        />
      ) : (
        <ModalFooter>
          <Button onClick={onCancel} mr="3">
            Cancel
          </Button>
          <Button
            isDisabled={!ready}
            isLoading={loading}
            colorScheme={trial ? 'green' : 'pink'}
            rightIcon={<FiArrowRight />}
            onClick={createPaymentMethod}
          >
            Next
          </Button>
        </ModalFooter>
      )}
    </>
  );
}

NewProjectForm.propTypes = {
  onCancel: PropTypes.func.isRequired
};

export default function NewProjectButton(props) {
  return (
    <ModalButton
      renderButton={openModal => (
        <Button
          colorScheme="pink"
          rounded="full"
          onClick={openModal}
          leftIcon={<FiPlus />}
          {...props}
        >
          New project
        </Button>
      )}
    >
      {closeModal => (
        <ModalContent>
          <ModalHeader>Create a project</ModalHeader>
          <ModalCloseButton />
          <NewProjectForm onCancel={closeModal} />
        </ModalContent>
      )}
    </ModalButton>
  );
}
